/**
 * Where an M&E indicator stands against its target.
 *
 * One function, used by the Outcomes page and by the funder report
 * drafter, so the percentage a programme manager sees is the percentage
 * the funder is told.
 *
 * Two kinds of indicator, and they are not measured the same way:
 *
 *   - A count that adds up over time (people trained, sessions run). The
 *     readings are what was delivered in each period and they are summed.
 *     The baseline is the reference point, usually last year's figure; it
 *     is not added to the count. Adding it reported 50 learners from last
 *     year as if they had been reached again this year.
 *   - A level where the latest reading is the truth (a rate, a median, a
 *     percentage, sites currently registered). Progress is how far the
 *     latest reading has moved from the baseline towards the target:
 *     (value - baseline) / (target - baseline). That works for a target
 *     below the baseline too, which is what "reduce waiting time from 40
 *     days to 10" is. The old value / target said a programme that had
 *     not moved off a baseline of 40 was 400% of the way to 10.
 */
const clamp01 = (n) => Math.min(1, Math.max(0, n));
export function indicatorStanding(indicator, readings) {
    const usable = readings.filter((r) => typeof r.value === 'number' && Number.isFinite(r.value));
    const baseline = Number.isFinite(indicator.baseline) ? indicator.baseline : 0;
    const target = Number.isFinite(indicator.target) ? indicator.target : 0;
    let latest = null;
    for (const r of usable) {
        if (!latest || (r.period ?? '') >= (latest.period ?? ''))
            latest = r;
    }
    const latestPeriod = latest?.period ?? null;
    if (indicator.aggregation === 'sum') {
        const value = usable.reduce((s, r) => s + r.value, 0);
        if (target < baseline) {
            // A ceiling on something that only adds up during the period. It
            // can be breached early but not "achieved" until the period ends,
            // so there is no progress bar to draw, only which side of it we are.
            // Checked before the "no target" case, because a target of zero
            // against a baseline above it ("no incidents this year") is a
            // ceiling, not a target nobody has set.
            return { value, latestPeriod, readings: usable.length, direction: 'falling', progress: null, met: value <= target };
        }
        if (!(target > 0)) {
            return { value, latestPeriod, readings: usable.length, direction: null, progress: null, met: null };
        }
        return {
            value,
            latestPeriod,
            readings: usable.length,
            direction: 'rising',
            progress: clamp01(value / target),
            met: value >= target,
        };
    }
    const value = latest ? latest.value : null;
    const direction = target > baseline ? 'rising' : target < baseline ? 'falling' : null;
    if (direction === null || value === null) {
        return { value, latestPeriod, readings: usable.length, direction, progress: null, met: null };
    }
    return {
        value,
        latestPeriod,
        readings: usable.length,
        direction,
        progress: clamp01((value - baseline) / (target - baseline)),
        met: direction === 'rising' ? value >= target : value <= target,
    };
}
